import { useState } from 'react'

import { Box, Checkbox, Text, useBreakpoint } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { ChevronLeft } from '@factorialco/f0-react/icons/app'
import { CreateUpgradePlanRequestDocument, NextInvoiceDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import LeftSection from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/LeftSection'
import PaymentDetail from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/PaymentDetail'
import useSelfUpsellPrices from 'modules/upsell/hooks/useSelfUpsellPrices'
import { LEGAL_LINKS } from 'modules/upsell/lib/legalLinks'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { formatDate } from 'modules/upsell/lib/selfUpsell/plans/utils'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'
import { readLatestUpsellOrigin } from 'modules/upselling_kit/utils/localStorage'

type TermsAndConditionsProps = {
  hasAcceptedTerms: boolean
  setHasAcceptedTerms: (hasAcceptedTerms: boolean) => void
  locale: string
  checkboxError: boolean
  setCheckboxError: (checkboxError: boolean) => void
}

const TermsAndConditions = ({
  hasAcceptedTerms,
  setHasAcceptedTerms,
  locale,
  checkboxError,
  setCheckboxError,
}: TermsAndConditionsProps) => {
  const getLegalLinks = (): { terms: string; privacy: string } => {
    if (LEGAL_LINKS[locale]) return LEGAL_LINKS[locale]
    const baseLocale = locale.split('-')[0] || 'en'

    return (
      LEGAL_LINKS[baseLocale] || {
        terms: 'https://factorialhr.com/terms-of-use',
        privacy: 'https://factorialhr.com/privacy',
      }
    )
  }

  const { terms, privacy } = getLegalLinks()

  const handleCheckboxChange = () => {
    setHasAcceptedTerms(!hasAcceptedTerms)
    setCheckboxError(false)
  }

  return (
    <Box flexDirection="row" gap="s8" alignItems="flexStart">
      <Checkbox checked={hasAcceptedTerms} onChange={handleCheckboxChange} size="large" />
      <Box alignSelf="start">
        <Box gap="s4">
          <Text.Normal weight="normal" color="grey800" size="200">
            {i18n.t('self_upsell.second_step.terms_checkbox_1')}{' '}
            <span
              onClick={() => {
                window.open(terms, '_blank')
              }}
            >
              <Text.Normal weight="medium" color="grey1000" size="200" underlined>
                {i18n.t('self_upsell.second_step.terms_checkbox_2')}
              </Text.Normal>
            </span>{' '}
            {i18n.t('self_upsell.second_step.terms_checkbox_3')}{' '}
            <span
              onClick={() => {
                window.open(privacy, '_blank')
              }}
            >
              <Text.Normal weight="medium" color="grey1000" size="200" underlined>
                {i18n.t('self_upsell.second_step.terms_checkbox_4')}
              </Text.Normal>
            </span>
            .
          </Text.Normal>
          {checkboxError && (
            <Text.Normal color="moodSuperNegative" size="100">
              {i18n.t('self_upsell.second_step.terms_checkbox_error')}
            </Text.Normal>
          )}
        </Box>
      </Box>
    </Box>
  )
}

type SecondStepProps = {
  addon: Plan
  step: string
  setStep: (step: string) => void
  moduleName: string
  installedAddonInternalId: string | null
}

const SecondStep = ({
  addon,
  step,
  setStep,
  moduleName,
  installedAddonInternalId,
}: SecondStepProps) => {
  const { company } = useCurrentSession()
  const { isMobile } = useBreakpoint()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [checkboxError, setCheckboxError] = useState(false)
  const [proratedPrice, setProratedPrice] = useState<{
    amount: number
    currency: string
    discounts: {
      discountPercentage: number
      amount: number
      discountType: string
    }[]
  } | null>(null)
  const { configPlan, period, currency, seats, shouldBypassSelfUpsell, currentSeats, isExpenses } =
    useSelfUpsellContext()
  const isOne = addon.internalTitle.toLowerCase().includes('ai credits')
  const shouldSendSeats = !isOne
  const seatsPayload = isExpenses() ? seats : currentSeats
  const { perSeatPrice, feePrice, stairstepPrice, totalPriceAfterDiscount } = useSelfUpsellPrices({
    addon,
    discounts: proratedPrice?.discounts,
    crossSellingAddons: [],
    mandatoryCrossSellingAddons: [],
  })
  const [fetchNextInvoiceData, { data: nextInvoiceData }] = useLazyQuery(NextInvoiceDocument)
  const nextInvoiceDate =
    nextInvoiceData?.billing.cpNextBillingEstimatesConnection.nodes[0]?.subscriptionEstimate
      ?.nextBillingAt

  const billingDate = nextInvoiceDate ? new Date(nextInvoiceDate * 1000) : new Date()
  const formattedBillingDate = formatDate(billingDate)

  const [upgradeLineItem] = useMutation(CreateUpgradePlanRequestDocument, {
    extractorFn: (data) => data.billingMutations.createBillingUpgradeLineItem,
  })

  const trackEvent = () => {
    const latestUpsellOrigin = readLatestUpsellOrigin()
    track(trackingEvents.upsell.clickConfirmUpgradeButton, {
      module: moduleName,
      selectedPlan: configPlan.variant,
      seats: seats,
      billingPeriod: period,
      upsellAmount: totalPriceAfterDiscount,
      upsellOrigin: latestUpsellOrigin?.request_source,
      component: 'self_upsell_upgrade_modal',
    })
  }

  const handleConfirm = async () => {
    setCheckboxError(false)
    if (!hasAcceptedTerms) setCheckboxError(true)
    if (!configPlan.addon || !hasAcceptedTerms || !configPlan.variant) return
    if (!installedAddonInternalId) {
      setStep('error')
      return
    }
    if (shouldBypassSelfUpsell) {
      setStep('success')
      return
    }

    trackEvent()

    const latestUpsellOrigin = readLatestUpsellOrigin()
    const upsellOrigin = latestUpsellOrigin?.request_source
    const upsellOriginCategory = latestUpsellOrigin?.request_source_category || 'Organic'
    const upsellOriginComponent = latestUpsellOrigin?.request_source_component
    const selfUpsellRequestSource = upsellOrigin || 'Marketplace'

    const buildAddonPayload = (itemPriceId: string) => ({
      itemPriceId,
      module: moduleName,
      ...(shouldSendSeats ? { seats: seatsPayload } : { seats: 0 }),
    })

    const result = await upgradeLineItem({
      variables: {
        oldAddons: [buildAddonPayload(installedAddonInternalId)],
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => buildAddonPayload(addonItem))
          : [buildAddonPayload(configPlan.addon)],
        origin: 'Self upsell - Marketplace detail page',
        upsellInfo: {
          itemName: `${addon.title} ${configPlan.variant}`,
          lineItemId: addon.pricing.getPlanId(period) || '',
          moduleRequested: moduleName,
          pricePerSeat: perSeatPrice === 0 ? undefined : perSeatPrice,
          priceFixed: feePrice === 0 ? undefined : feePrice,
          priceStairstep: stairstepPrice === 0 ? undefined : stairstepPrice,
          proratedPrice: proratedPrice?.amount ? Number(proratedPrice.amount) : undefined,
          seats: seatsPayload,
          currency: currency,
          totalPrice: totalPriceAfterDiscount,
          billingDate: formattedBillingDate,
          billingPeriod: period,
          requestSource: selfUpsellRequestSource,
          requestSourceCategory: upsellOriginCategory,
          requestSourceComponent: upsellOriginComponent,
          upsellRequestType: 'Self-upsell',
        },
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      if (handled.data?.upgradeLineItem?.action === 'upsell-created') {
        setStep('upsell-created')
      } else {
        setStep('success')
      }
    } else {
      setStep('error')
    }
  }

  useEffect(() => {
    if (shouldBypassSelfUpsell) return

    fetchNextInvoiceData({
      variables: { companyId: company.id },
    })
  }, [seats, currency, shouldBypassSelfUpsell])

  return (
    <Box flexDirection="row" height="full">
      {!isMobile && (
        <LeftSection addon={addon} variant={configPlan.variant} step={step} iconName={moduleName} />
      )}
      <Box
        padding="s24"
        justifyContent="spaceBetween"
        style={{
          width: isMobile ? '100%' : '60%',
        }}
      >
        <Box gap="s16">
          <Box height="s24" />
          {isMobile && (
            <Text size="400" color="grey1000" weight="semibold">
              {addon.title}
            </Text>
          )}
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('self_upsell.second_step.order_summary')}
          </Text.Normal>
          <PaymentDetail
            addon={addon}
            billingDate={formattedBillingDate}
            discounts={proratedPrice?.discounts}
            loadingPrice={false}
          />
          <TermsAndConditions
            hasAcceptedTerms={hasAcceptedTerms}
            setHasAcceptedTerms={setHasAcceptedTerms}
            locale={company.locale || 'en'}
            checkboxError={checkboxError}
            setCheckboxError={setCheckboxError}
          />
        </Box>

        <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
          <F0Button
            hideLabel
            icon={ChevronLeft}
            variant="outline"
            round
            size="lg"
            onClick={() => setStep('first')}
          />
          <F0Button
            size="lg"
            label={i18n.t('self_upsell.second_step.button')}
            onClick={handleConfirm}
            disabled={!configPlan.addon}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default SecondStep
